import { renderEmailLayout } from './emailLayout';

export interface EmailTemplate {
  subject: string;
  html: string;
  text: string;
}

interface BaseTemplateOptions {
  preheader?: string;
  title: string;
  content: string;
  actionButton?: {
    text: string;
    url: string;
  };
  secondaryButton?: {
    text: string;
    url: string;
  };
  footer?: string;
  accent?: 'primary' | 'success' | 'warning' | 'danger';
}

class EmailTemplateService {
  private readonly brandName = 'Cero Ideas CRM';
  private readonly baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://crm.ceroideas.es';

  // Reescrito 2026-09-19: delega en renderEmailLayout compartido para
  // que todos los emails (server-side y client-side) usen el mismo look.
  // Mantiene la interfaz vieja `content / actionButton / secondaryButton`
  // para no romper las 14 plantillas que le llaman.
  private getBaseTemplate(options: BaseTemplateOptions): string {
    // La firma vieja permitía styling con clases (.text, .divider, .badge,
    // etc.). Traducimos esas clases a inline-style equivalentes para que el
    // HTML funcione bien también en clientes de email que ignoran <style>.
    const styleShim = `
      <style>
        .text { font-size: 15px; color: #374151; line-height: 1.6; margin: 0 0 12px 0; }
        .divider { border-top: 1px solid #e5e7eb; margin: 18px 0; }
        .badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 13px; font-weight: 600; }
        .badge-success { background-color: #ecfdf5; color: #065f46; }
        .badge-warning { background-color: #fffbeb; color: #92400e; }
        .badge-danger { background-color: #fef2f2; color: #991b1b; }
        .badge-info { background-color: #eff6ff; color: #1e40af; }
        table { width: 100%; border-collapse: collapse; margin: 12px 0; }
        th, td { padding: 8px 4px; text-align: left; vertical-align: top; font-size: 14px; color: #374151; }
        th { background-color: #f9fafb; font-weight: 600; color: #111827; }
      </style>
    `;

    const secondaryLine = options.secondaryButton ? `
      <p style="margin: 12px 0 0 0; text-align: center; font-size: 13px; color: #6b7280;">
        o
        <a href="${options.secondaryButton.url}" style="color: #2563eb; text-decoration: none; font-weight: 600;">
          ${options.secondaryButton.text}
        </a>
      </p>
    ` : '';

    return renderEmailLayout({
      preheader: options.preheader,
      title: options.title,
      accent: options.accent || 'primary',
      body: styleShim + options.content + secondaryLine,
      cta: options.actionButton ? {
        label: options.actionButton.text,
        url: options.actionButton.url,
      } : undefined,
      footerNote: options.footer ? options.footer.replace(/<[^>]+>/g, '').trim() : undefined,
    });
  }

  private stripHtml(html: string): string {
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  newUserWelcome(userName: string, userEmail: string, temporaryPassword: string, resetLink: string): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${userName}</strong>,</p>
      <p class="text">Tu cuenta en ${this.brandName} ha sido creada exitosamente por el administrador del sistema.</p>
      <div class="divider"></div>
      <p class="text"><strong>Credenciales de acceso:</strong></p>
      <table>
        <tr>
          <td style="width: 140px;"><strong>Email:</strong></td>
          <td>${userEmail}</td>
        </tr>
        <tr>
          <td><strong>Contraseña temporal:</strong></td>
          <td><code style="background-color: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-family: monospace;">${temporaryPassword}</code></td>
        </tr>
      </table>
      <div class="divider"></div>
      <p class="text">⚠️ <strong>Importante:</strong> Por tu seguridad, debes cambiar esta contraseña en tu primer acceso.</p>
      <p class="text">Haz clic en el botón de abajo para acceder al sistema y cambiar tu contraseña:</p>
    `;

    const html = this.getBaseTemplate({
      preheader: 'Tu cuenta ha sido creada. Accede y cambia tu contraseña.',
      title: '¡Bienvenido a Cero Ideas CRM!',
      content,
      actionButton: {
        text: 'Cambiar Contraseña',
        url: resetLink
      },
      secondaryButton: {
        text: 'Acceder al CRM',
        url: this.baseUrl
      },
      footer: '<p style="margin: 8px 0 0 0; font-size: 12px;">Si no solicitaste esta cuenta, por favor contacta a soporte inmediatamente.</p>'
    });

    const text = `
Bienvenido a ${this.brandName}

Hola ${userName},

Tu cuenta ha sido creada exitosamente por el administrador del sistema.

Credenciales de acceso:
- Email: ${userEmail}
- Contraseña temporal: ${temporaryPassword}

IMPORTANTE: Por tu seguridad, debes cambiar esta contraseña en tu primer acceso.

Cambiar contraseña: ${resetLink}
Acceder al CRM: ${this.baseUrl}

Si no solicitaste esta cuenta, contacta a soporte inmediatamente.

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `Bienvenido a ${this.brandName} - Cambia tu contraseña`,
      html,
      text
    };
  }

  passwordReset(userName: string, resetLink: string, expiresIn: string = '24 horas'): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${userName}</strong>,</p>
      <p class="text">Se ha solicitado un cambio de contraseña para tu cuenta en ${this.brandName}.</p>
      <p class="text">Haz clic en el botón de abajo para establecer una nueva contraseña:</p>
      <div class="divider"></div>
      <p class="text" style="font-size: 14px; color: #6b7280;">Este enlace expirará en <strong>${expiresIn}</strong>.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: 'Restablece tu contraseña de acceso',
      title: 'Restablecer Contraseña',
      content,
      actionButton: {
        text: 'Cambiar Contraseña',
        url: resetLink
      },
      footer: '<p style="margin: 8px 0 0 0; font-size: 12px;">Si no solicitaste este cambio, ignora este email. Tu contraseña permanecerá sin cambios.</p>'
    });

    const text = `
Restablecer Contraseña - ${this.brandName}

Hola ${userName},

Se ha solicitado un cambio de contraseña para tu cuenta.

Cambiar contraseña: ${resetLink}

Este enlace expirará en ${expiresIn}.

Si no solicitaste este cambio, ignora este email.

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: 'Restablecer tu contraseña',
      html,
      text
    };
  }

  timesheetApproved(
    collaboratorName: string,
    projectName: string,
    hours: number,
    amount: number,
    date: Date,
    approvedBy: string,
    viewUrl: string,
    downloadUrl?: string
  ): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${collaboratorName}</strong>,</p>
      <p class="text">¡Buenas noticias! Tu parte de horas ha sido <span class="badge badge-success">Aprobado</span>.</p>
      <div class="divider"></div>
      <table>
        <tr>
          <th colspan="2" style="background-color: #d1fae5; color: #065f46;">Detalles del Parte Aprobado</th>
        </tr>
        <tr>
          <td style="width: 140px;"><strong>Proyecto:</strong></td>
          <td>${projectName}</td>
        </tr>
        <tr>
          <td><strong>Fecha:</strong></td>
          <td>${date.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</td>
        </tr>
        <tr>
          <td><strong>Horas:</strong></td>
          <td>${hours}h</td>
        </tr>
        <tr>
          <td><strong>Importe:</strong></td>
          <td style="font-size: 18px; font-weight: 600; color: #065f46;">${amount.toLocaleString('es-ES')}€</td>
        </tr>
        <tr>
          <td><strong>Aprobado por:</strong></td>
          <td>${approvedBy}</td>
        </tr>
      </table>
      <p class="text">El importe será incluido en tu próxima facturación.</p>
      ${downloadUrl ? '<p class="text">Puedes descargar el comprobante en PDF usando el botón de abajo.</p>' : ''}
    `;

    const html = this.getBaseTemplate({
      preheader: `Tu parte de ${hours}h ha sido aprobado - ${amount}€`,
      title: '✅ Parte de Horas Aprobado',
      content,
      actionButton: {
        text: 'Ver Parte de Horas',
        url: viewUrl
      },
      secondaryButton: downloadUrl ? {
        text: 'Descargar PDF',
        url: downloadUrl
      } : undefined
    });

    const text = `
Parte de Horas Aprobado - ${this.brandName}

Hola ${collaboratorName},

¡Buenas noticias! Tu parte de horas ha sido aprobado.

Detalles:
- Proyecto: ${projectName}
- Fecha: ${date.toLocaleDateString('es-ES')}
- Horas: ${hours}h
- Importe: ${amount}€
- Aprobado por: ${approvedBy}

Ver detalles: ${viewUrl}
${downloadUrl ? `Descargar PDF: ${downloadUrl}` : ''}

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `✅ Parte de Horas Aprobado - ${amount}€`,
      html,
      text
    };
  }

  timesheetRejected(
    collaboratorName: string,
    projectName: string,
    hours: number,
    date: Date,
    reason: string,
    editUrl: string
  ): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${collaboratorName}</strong>,</p>
      <p class="text">Tu parte de horas ha sido <span class="badge badge-danger">Rechazado</span>.</p>
      <div class="divider"></div>
      <table>
        <tr>
          <th colspan="2" style="background-color: #fee2e2; color: #991b1b;">Detalles del Parte</th>
        </tr>
        <tr>
          <td style="width: 140px;"><strong>Proyecto:</strong></td>
          <td>${projectName}</td>
        </tr>
        <tr>
          <td><strong>Fecha:</strong></td>
          <td>${date.toLocaleDateString('es-ES')}</td>
        </tr>
        <tr>
          <td><strong>Horas:</strong></td>
          <td>${hours}h</td>
        </tr>
      </table>
      ${reason ? `
        <div style="background-color: #fef2f2; border-left: 4px solid #ef4444; padding: 16px; margin: 16px 0; border-radius: 4px;">
          <p style="margin: 0; font-weight: 600; color: #991b1b;">Motivo del rechazo:</p>
          <p style="margin: 8px 0 0 0; color: #6b7280;">${reason}</p>
        </div>
      ` : ''}
      <p class="text">Por favor, revisa y corrige el parte de horas, luego vuelve a enviarlo.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: `Tu parte de ${hours}h ha sido rechazado`,
      title: '❌ Parte de Horas Rechazado',
      content,
      actionButton: {
        text: 'Editar y Reenviar',
        url: editUrl
      }
    });

    const text = `
Parte de Horas Rechazado - ${this.brandName}

Hola ${collaboratorName},

Tu parte de horas ha sido rechazado.

Detalles:
- Proyecto: ${projectName}
- Fecha: ${date.toLocaleDateString('es-ES')}
- Horas: ${hours}h

${reason ? `Motivo del rechazo:\n${reason}\n` : ''}

Por favor, revisa y corrige el parte, luego vuelve a enviarlo.

Editar: ${editUrl}

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `❌ Parte de Horas Rechazado - ${projectName}`,
      html,
      text
    };
  }

  invoiceValidated(
    collaboratorName: string,
    invoiceNumber: string,
    amount: number,
    currency: string,
    validatedBy: string,
    validatedAt: Date,
    viewUrl: string,
    downloadUrl?: string
  ): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${collaboratorName}</strong>,</p>
      <p class="text">Tu factura ha sido <span class="badge badge-success">Validada</span> y está en proceso de pago.</p>
      <div class="divider"></div>
      <table>
        <tr>
          <th colspan="2" style="background-color: #dbeafe; color: #1e40af;">Detalles de la Factura</th>
        </tr>
        <tr>
          <td style="width: 160px;"><strong>Número de factura:</strong></td>
          <td><code style="background-color: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-family: monospace;">${invoiceNumber}</code></td>
        </tr>
        <tr>
          <td><strong>Importe:</strong></td>
          <td style="font-size: 18px; font-weight: 600; color: #1e40af;">${amount.toLocaleString('es-ES')} ${currency}</td>
        </tr>
        <tr>
          <td><strong>Validada por:</strong></td>
          <td>${validatedBy}</td>
        </tr>
        <tr>
          <td><strong>Fecha de validación:</strong></td>
          <td>${validatedAt.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</td>
        </tr>
      </table>
      <p class="text">Recibirás una notificación cuando el pago sea procesado.</p>
      ${downloadUrl ? '<p class="text">Puedes descargar tu factura en PDF usando el botón de abajo.</p>' : ''}
    `;

    const html = this.getBaseTemplate({
      preheader: `Factura ${invoiceNumber} validada - ${amount} ${currency}`,
      title: '✅ Factura Validada',
      content,
      actionButton: {
        text: 'Ver Factura',
        url: viewUrl
      },
      secondaryButton: downloadUrl ? {
        text: 'Descargar PDF',
        url: downloadUrl
      } : undefined
    });

    const text = `
Factura Validada - ${this.brandName}

Hola ${collaboratorName},

Tu factura ha sido validada y está en proceso de pago.

Detalles:
- Número: ${invoiceNumber}
- Importe: ${amount} ${currency}
- Validada por: ${validatedBy}
- Fecha: ${validatedAt.toLocaleDateString('es-ES')}

Recibirás una notificación cuando el pago sea procesado.

Ver factura: ${viewUrl}
${downloadUrl ? `Descargar PDF: ${downloadUrl}` : ''}

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `✅ Factura Validada - ${invoiceNumber}`,
      html,
      text
    };
  }

  invoiceRejected(
    collaboratorName: string,
    invoiceNumber: string,
    amount: number,
    currency: string,
    reason: string,
    rejectedBy: string,
    viewUrl: string
  ): EmailTemplate {
    const escaped = reason
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br/>');
    const content = `
      <p class="text">Hola <strong>${collaboratorName}</strong>,</p>
      <p class="text">Tu factura ha sido <span class="badge badge-danger">Rechazada</span>. Puedes corregirla y volver a enviarla desde el panel.</p>
      <div class="divider"></div>
      <table>
        <tr>
          <th colspan="2" style="background-color: #fee2e2; color: #991b1b;">Detalles de la Factura</th>
        </tr>
        <tr>
          <td style="width: 160px;"><strong>Número de factura:</strong></td>
          <td><code style="background-color: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-family: monospace;">${invoiceNumber}</code></td>
        </tr>
        <tr>
          <td><strong>Importe:</strong></td>
          <td style="font-size: 18px; font-weight: 600; color: #991b1b;">${amount.toLocaleString('es-ES')} ${currency}</td>
        </tr>
        <tr>
          <td><strong>Rechazada por:</strong></td>
          <td>${rejectedBy}</td>
        </tr>
      </table>
      <div style="margin-top: 16px; padding: 12px 16px; background-color: #fef2f2; border-left: 4px solid #dc2626; border-radius: 4px;">
        <p style="margin: 0; font-weight: 600; color: #991b1b;">Motivo del rechazo</p>
        <p style="margin: 8px 0 0 0; color: #7f1d1d;">${escaped}</p>
      </div>
      <p class="text" style="margin-top: 16px;">Entra al panel, corrige los datos y vuelve a enviar la factura para su revisión.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: `Factura ${invoiceNumber} rechazada`,
      title: '✕ Factura Rechazada',
      content,
      actionButton: {
        text: 'Corregir factura',
        url: viewUrl,
      },
    });

    const text = `
Factura Rechazada - ${this.brandName}

Hola ${collaboratorName},

Tu factura ${invoiceNumber} (${amount} ${currency}) ha sido rechazada por ${rejectedBy}.

Motivo:
${reason}

Corrige los datos y vuelve a enviarla desde: ${viewUrl}

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `✕ Factura Rechazada - ${invoiceNumber}`,
      html,
      text,
    };
  }

  invoicePaid(
    collaboratorName: string,
    invoiceNumber: string,
    amount: number,
    currency: string,
    paidAt: Date,
    paymentMethod: string,
    viewUrl: string
  ): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${collaboratorName}</strong>,</p>
      <p class="text">¡Excelente noticia! Tu factura ha sido <span class="badge badge-success">Pagada</span>.</p>
      <div class="divider"></div>
      <table>
        <tr>
          <th colspan="2" style="background-color: #d1fae5; color: #065f46;">Confirmación de Pago</th>
        </tr>
        <tr>
          <td style="width: 160px;"><strong>Número de factura:</strong></td>
          <td><code style="background-color: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-family: monospace;">${invoiceNumber}</code></td>
        </tr>
        <tr>
          <td><strong>Importe pagado:</strong></td>
          <td style="font-size: 20px; font-weight: 700; color: #065f46;">${amount.toLocaleString('es-ES')} ${currency}</td>
        </tr>
        <tr>
          <td><strong>Fecha de pago:</strong></td>
          <td>${paidAt.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</td>
        </tr>
        <tr>
          <td><strong>Método de pago:</strong></td>
          <td>${paymentMethod}</td>
        </tr>
      </table>
      <p class="text">El pago debería reflejarse en tu cuenta en los próximos días hábiles.</p>
      <p class="text">Gracias por tu colaboración con ${this.brandName}.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: `Pago procesado: ${amount} ${currency}`,
      title: '💰 Factura Pagada',
      content,
      actionButton: {
        text: 'Ver Comprobante',
        url: viewUrl
      }
    });

    const text = `
Factura Pagada - ${this.brandName}

Hola ${collaboratorName},

¡Excelente noticia! Tu factura ha sido pagada.

Detalles del pago:
- Número de factura: ${invoiceNumber}
- Importe: ${amount} ${currency}
- Fecha de pago: ${paidAt.toLocaleDateString('es-ES')}
- Método: ${paymentMethod}

El pago debería reflejarse en tu cuenta en los próximos días hábiles.

Ver comprobante: ${viewUrl}

Gracias por tu colaboración con ${this.brandName}.

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `💰 Pago Procesado - ${invoiceNumber} - ${amount} ${currency}`,
      html,
      text
    };
  }

  dailyTimesheetsDigest(
    adminName: string,
    timesheets: Array<{
      id: string;
      collaboratorName: string;
      projectName: string;
      hours: number;
      amount: number;
      date: Date;
    }>,
    totalHours: number,
    totalAmount: number,
    viewAllUrl: string
  ): EmailTemplate {
    const timesheetRows = timesheets.map(ts => `
      <tr>
        <td>${ts.collaboratorName}</td>
        <td>${ts.projectName}</td>
        <td>${ts.date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}</td>
        <td style="text-align: right;">${ts.hours}h</td>
        <td style="text-align: right; font-weight: 600;">${ts.amount.toLocaleString('es-ES')}€</td>
      </tr>
    `).join('');

    const content = `
      <p class="text">Hola <strong>${adminName}</strong>,</p>
      <p class="text">Aquí está el resumen de <strong>${timesheets.length}</strong> parte(s) de horas enviado(s) hoy:</p>
      <table>
        <thead>
          <tr>
            <th>Colaborador</th>
            <th>Proyecto</th>
            <th>Fecha</th>
            <th style="text-align: right;">Horas</th>
            <th style="text-align: right;">Importe</th>
          </tr>
        </thead>
        <tbody>
          ${timesheetRows}
          <tr style="background-color: #f9fafb; font-weight: 600;">
            <td colspan="3" style="text-align: right;">TOTAL:</td>
            <td style="text-align: right;">${totalHours}h</td>
            <td style="text-align: right; color: #2563eb; font-size: 18px;">${totalAmount.toLocaleString('es-ES')}€</td>
          </tr>
        </tbody>
      </table>
      <p class="text">Revisa y aprueba los partes de horas pendientes en el panel de administración.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: `${timesheets.length} partes de horas pendientes de revisión`,
      title: `📊 Resumen Diario - ${timesheets.length} Partes de Horas`,
      content,
      actionButton: {
        text: 'Revisar Partes',
        url: viewAllUrl
      }
    });

    const timesheetsText = timesheets.map(ts =>
      `- ${ts.collaboratorName} | ${ts.projectName} | ${ts.hours}h | ${ts.amount}€`
    ).join('\n');

    const text = `
Resumen Diario - Partes de Horas (${this.brandName})

Hola ${adminName},

Resumen de ${timesheets.length} parte(s) de horas enviado(s) hoy:

${timesheetsText}

TOTAL: ${totalHours}h | ${totalAmount}€

Revisa y aprueba los partes en el panel de administración.

Ver todos: ${viewAllUrl}

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `📊 Resumen Diario: ${timesheets.length} Partes de Horas (${totalAmount}€)`,
      html,
      text
    };
  }

  dailyInvoicesDigest(
    adminName: string,
    invoices: Array<{
      id: string;
      collaboratorName: string;
      invoiceNumber: string;
      amount: number;
      currency: string;
      date: Date;
    }>,
    totalAmount: number,
    viewAllUrl: string
  ): EmailTemplate {
    const invoiceRows = invoices.map(inv => `
      <tr>
        <td>${inv.collaboratorName}</td>
        <td><code style="background-color: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-size: 12px;">${inv.invoiceNumber}</code></td>
        <td>${inv.date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}</td>
        <td style="text-align: right; font-weight: 600;">${inv.amount.toLocaleString('es-ES')} ${inv.currency}</td>
      </tr>
    `).join('');

    const content = `
      <p class="text">Hola <strong>${adminName}</strong>,</p>
      <p class="text">Aquí está el resumen de <strong>${invoices.length}</strong> factura(s) recibida(s) hoy:</p>
      <table>
        <thead>
          <tr>
            <th>Colaborador</th>
            <th>Nº Factura</th>
            <th>Fecha</th>
            <th style="text-align: right;">Importe</th>
          </tr>
        </thead>
        <tbody>
          ${invoiceRows}
          <tr style="background-color: #f9fafb; font-weight: 600;">
            <td colspan="3" style="text-align: right;">TOTAL:</td>
            <td style="text-align: right; color: #2563eb; font-size: 18px;">${totalAmount.toLocaleString('es-ES')} EUR</td>
          </tr>
        </tbody>
      </table>
      <p class="text">Revisa y valida las facturas pendientes en el panel de administración.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: `${invoices.length} facturas pendientes de validación`,
      title: `📄 Resumen Diario - ${invoices.length} Facturas`,
      content,
      actionButton: {
        text: 'Revisar Facturas',
        url: viewAllUrl
      }
    });

    const invoicesText = invoices.map(inv =>
      `- ${inv.collaboratorName} | ${inv.invoiceNumber} | ${inv.amount} ${inv.currency}`
    ).join('\n');

    const text = `
Resumen Diario - Facturas (${this.brandName})

Hola ${adminName},

Resumen de ${invoices.length} factura(s) recibida(s) hoy:

${invoicesText}

TOTAL: ${totalAmount} EUR

Revisa y valida las facturas en el panel de administración.

Ver todas: ${viewAllUrl}

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `📄 Resumen Diario: ${invoices.length} Facturas (${totalAmount}€)`,
      html,
      text
    };
  }

  registrationRequestPending(
    adminName: string,
    requestName: string,
    requestEmail: string,
    requestedAt: Date,
    approveUrl: string,
    rejectUrl: string
  ): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${adminName}</strong>,</p>
      <p class="text">Hay una nueva <span class="badge badge-warning">Solicitud de Registro</span> pendiente de revisión.</p>
      <div class="divider"></div>
      <table>
        <tr>
          <th colspan="2" style="background-color: #fef3c7; color: #92400e;">Datos del Solicitante</th>
        </tr>
        <tr>
          <td style="width: 140px;"><strong>Nombre:</strong></td>
          <td>${requestName}</td>
        </tr>
        <tr>
          <td><strong>Email:</strong></td>
          <td>${requestEmail}</td>
        </tr>
        <tr>
          <td><strong>Fecha solicitud:</strong></td>
          <td>${requestedAt.toLocaleString('es-ES', { dateStyle: 'full', timeStyle: 'short' })}</td>
        </tr>
      </table>
      <p class="text">Por favor, revisa esta solicitud y decide si aprobarla o rechazarla.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: `Nueva solicitud de ${requestName}`,
      title: '🔔 Nueva Solicitud de Registro',
      content,
      actionButton: {
        text: 'Revisar Solicitud',
        url: approveUrl
      }
    });

    const text = `
Nueva Solicitud de Registro - ${this.brandName}

Hola ${adminName},

Hay una nueva solicitud de registro pendiente de revisión.

Datos del solicitante:
- Nombre: ${requestName}
- Email: ${requestEmail}
- Fecha: ${requestedAt.toLocaleString('es-ES')}

Revisar solicitud: ${approveUrl}

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `🔔 Nueva Solicitud de Registro - ${requestName}`,
      html,
      text
    };
  }

  collaboratorInvitation(
    collaboratorName: string,
    invitedBy: string,
    roleName: string,
    activationUrl: string,
    expiresIn: string = '7 días'
  ): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${collaboratorName}</strong>,</p>
      <p class="text"><strong>${invitedBy}</strong> te ha invitado a unirte a ${this.brandName} como <span class="badge badge-info">${roleName}</span>.</p>
      <div class="divider"></div>
      <div style="background-color: #dbeafe; border-left: 4px solid #2563eb; padding: 16px; margin: 20px 0; border-radius: 4px;">
        <p style="margin: 0; font-weight: 600; color: #1e40af;">Próximos pasos:</p>
        <ol style="margin: 10px 0 0 0; padding-left: 20px; color: #1e3a8a;">
          <li>Haz clic en el botón "Activar Cuenta"</li>
          <li>Crea tu contraseña segura</li>
          <li>Completa tu perfil</li>
          <li>Comienza a usar el CRM</li>
        </ol>
      </div>
      <p class="text" style="font-size: 14px; color: #6b7280;">⏰ Esta invitación expirará en <strong>${expiresIn}</strong>.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: `${invitedBy} te ha invitado a unirte a ${this.brandName}`,
      title: '¡Te han invitado al CRM!',
      content,
      actionButton: {
        text: 'Activar Cuenta',
        url: activationUrl
      },
      footer: '<p style="margin: 8px 0 0 0; font-size: 12px;">Si no esperabas esta invitación, puedes ignorar este email.</p>'
    });

    const text = `
Invitación a ${this.brandName}

Hola ${collaboratorName},

${invitedBy} te ha invitado a unirte a ${this.brandName} como ${roleName}.

Próximos pasos:
1. Accede al enlace de activación
2. Crea tu contraseña segura
3. Completa tu perfil
4. Comienza a usar el CRM

Activar cuenta: ${activationUrl}

Esta invitación expirará en ${expiresIn}.

Si no esperabas esta invitación, puedes ignorar este email.

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `Invitación a ${this.brandName} - ${roleName}`,
      html,
      text
    };
  }

  accountActivated(
    userName: string,
    roleName: string,
    loginUrl: string
  ): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${userName}</strong>,</p>
      <p class="text">¡Tu cuenta ha sido activada exitosamente! Ya puedes acceder al CRM de ${this.brandName}.</p>
      <div class="divider"></div>
      <table>
        <tr>
          <th colspan="2" style="background-color: #d1fae5; color: #065f46;">Información de tu Cuenta</th>
        </tr>
        <tr>
          <td style="width: 140px;"><strong>Rol asignado:</strong></td>
          <td><span class="badge badge-success">${roleName}</span></td>
        </tr>
        <tr>
          <td><strong>Estado:</strong></td>
          <td><span class="badge badge-success">Activa</span></td>
        </tr>
      </table>
      <div class="divider"></div>
      <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 16px; margin: 20px 0; border-radius: 4px;">
        <p style="margin: 0; font-weight: 600; color: #92400e;">Recomendaciones para empezar:</p>
        <ul style="margin: 10px 0 0 0; padding-left: 20px; color: #78350f;">
          <li>Completa tu perfil con tus datos personales</li>
          <li>Configura tus preferencias de notificación</li>
          <li>Explora las funcionalidades disponibles para tu rol</li>
          <li>Familiarízate con el sistema de gestión</li>
        </ul>
      </div>
    `;

    const html = this.getBaseTemplate({
      preheader: 'Tu cuenta está activa. Bienvenido al equipo.',
      title: '¡Cuenta Activada con Éxito!',
      content,
      actionButton: {
        text: 'Iniciar Sesión',
        url: loginUrl
      },
      footer: '<p style="margin: 8px 0 0 0; font-size: 12px;">Si necesitas ayuda, contacta con tu administrador.</p>'
    });

    const text = `
Cuenta Activada - ${this.brandName}

Hola ${userName},

¡Tu cuenta ha sido activada exitosamente! Ya puedes acceder al CRM.

Información de tu cuenta:
- Rol: ${roleName}
- Estado: Activa

Recomendaciones para empezar:
- Completa tu perfil con tus datos personales
- Configura tus preferencias de notificación
- Explora las funcionalidades disponibles
- Familiarízate con el sistema

Iniciar sesión: ${loginUrl}

Si necesitas ayuda, contacta con tu administrador.

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `¡Bienvenido a ${this.brandName}! - Cuenta Activada`,
      html,
      text
    };
  }

  passwordChangedConfirmation(
    userName: string,
    changedAt: Date,
    ipAddress?: string,
    supportUrl?: string
  ): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${userName}</strong>,</p>
      <p class="text">Tu contraseña ha sido <span class="badge badge-success">cambiada exitosamente</span>.</p>
      <div class="divider"></div>
      <table>
        <tr>
          <th colspan="2" style="background-color: #dbeafe; color: #1e40af;">Detalles del Cambio</th>
        </tr>
        <tr>
          <td style="width: 140px;"><strong>Fecha y hora:</strong></td>
          <td>${changedAt.toLocaleString('es-ES', { dateStyle: 'full', timeStyle: 'medium' })}</td>
        </tr>
        ${ipAddress ? `
        <tr>
          <td><strong>Dirección IP:</strong></td>
          <td><code style="background-color: #f3f4f6; padding: 4px 8px; border-radius: 4px; font-family: monospace;">${ipAddress}</code></td>
        </tr>
        ` : ''}
      </table>
      <div class="divider"></div>
      <div style="background-color: #fef2f2; border-left: 4px solid #ef4444; padding: 16px; margin: 20px 0; border-radius: 4px;">
        <p style="margin: 0; font-weight: 600; color: #991b1b;">¿No fuiste tú?</p>
        <p style="margin: 8px 0 0 0; color: #6b7280;">Si no realizaste este cambio, tu cuenta podría estar comprometida. ${supportUrl ? `Contacta inmediatamente con soporte haciendo clic en el botón de abajo.` : `Contacta inmediatamente con tu administrador.`}</p>
      </div>
      <p class="text">Si fuiste tú quien realizó este cambio, puedes ignorar este mensaje.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: 'Tu contraseña ha sido modificada',
      title: 'Contraseña Cambiada',
      content,
      actionButton: supportUrl ? {
        text: 'Reportar Problema',
        url: supportUrl
      } : undefined,
      footer: '<p style="margin: 8px 0 0 0; font-size: 12px;">Este email se envía automáticamente como medida de seguridad.</p>'
    });

    const text = `
Contraseña Cambiada - ${this.brandName}

Hola ${userName},

Tu contraseña ha sido cambiada exitosamente.

Detalles del cambio:
- Fecha: ${changedAt.toLocaleString('es-ES')}
${ipAddress ? `- IP: ${ipAddress}` : ''}

¿No fuiste tú?
Si no realizaste este cambio, tu cuenta podría estar comprometida. Contacta inmediatamente con soporte.

${supportUrl ? `Reportar problema: ${supportUrl}` : ''}

Si fuiste tú, puedes ignorar este mensaje.

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: 'Contraseña Cambiada - Confirmación de Seguridad',
      html,
      text
    };
  }

  meetingReminder(
    userName: string,
    meetingTitle: string,
    meetingDate: Date,
    duration: number,
    location: string,
    attendees: string[],
    description: string,
    viewUrl: string,
    rescheduleUrl?: string
  ): EmailTemplate {
    const attendeesList = attendees.map(a => `<li style="color: #6b7280;">${a}</li>`).join('');

    const content = `
      <p class="text">Hola <strong>${userName}</strong>,</p>
      <p class="text">Recordatorio: Tienes una reunión programada para <strong>mañana</strong>.</p>
      <div class="divider"></div>
      <table>
        <tr>
          <th colspan="2" style="background-color: #dbeafe; color: #1e40af;">Detalles de la Reunión</th>
        </tr>
        <tr>
          <td style="width: 140px;"><strong>Título:</strong></td>
          <td style="font-weight: 600;">${meetingTitle}</td>
        </tr>
        <tr>
          <td><strong>Fecha y hora:</strong></td>
          <td>${meetingDate.toLocaleString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
        </tr>
        <tr>
          <td><strong>Duración:</strong></td>
          <td>${duration} minutos</td>
        </tr>
        <tr>
          <td><strong>Ubicación:</strong></td>
          <td>${location}</td>
        </tr>
      </table>
      ${attendees.length > 0 ? `
        <p class="text" style="margin-top: 20px;"><strong>Participantes:</strong></p>
        <ul style="margin: 8px 0; padding-left: 20px;">
          ${attendeesList}
        </ul>
      ` : ''}
      ${description ? `
        <div class="divider"></div>
        <p class="text"><strong>Descripción:</strong></p>
        <p class="text" style="color: #6b7280;">${description}</p>
      ` : ''}
      <div class="divider"></div>
      <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 16px; margin: 20px 0; border-radius: 4px;">
        <p style="margin: 0; font-size: 14px; color: #92400e;">⏰ <strong>La reunión es mañana.</strong> Asegúrate de estar preparado y llegar a tiempo.</p>
      </div>
    `;

    const html = this.getBaseTemplate({
      preheader: `Recordatorio: ${meetingTitle} mañana`,
      title: '📅 Recordatorio de Reunión',
      content,
      actionButton: {
        text: 'Ver Detalles',
        url: viewUrl
      },
      secondaryButton: rescheduleUrl ? {
        text: 'Reprogramar',
        url: rescheduleUrl
      } : undefined
    });

    const attendeesText = attendees.map(a => `- ${a}`).join('\n');

    const text = `
Recordatorio de Reunión - ${this.brandName}

Hola ${userName},

Recordatorio: Tienes una reunión programada para mañana.

Detalles de la reunión:
- Título: ${meetingTitle}
- Fecha: ${meetingDate.toLocaleString('es-ES')}
- Duración: ${duration} minutos
- Ubicación: ${location}

${attendees.length > 0 ? `Participantes:\n${attendeesText}\n` : ''}
${description ? `\nDescripción:\n${description}\n` : ''}

La reunión es mañana. Asegúrate de estar preparado.

Ver detalles: ${viewUrl}
${rescheduleUrl ? `Reprogramar: ${rescheduleUrl}` : ''}

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `📅 Recordatorio: ${meetingTitle} - Mañana`,
      html,
      text
    };
  }

  newLeadNotification(
    adminName: string,
    leadName: string,
    leadEmail: string,
    leadPhone: string,
    leadSource: string,
    leadMessage: string,
    leadUrl: string
  ): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${adminName}</strong>,</p>
      <p class="text">¡Nuevo lead registrado! <span class="badge badge-success">Requiere atención</span></p>
      <div class="divider"></div>
      <table>
        <tr>
          <th colspan="2" style="background-color: #d1fae5; color: #065f46;">Información del Lead</th>
        </tr>
        <tr>
          <td style="width: 140px;"><strong>Nombre:</strong></td>
          <td style="font-weight: 600;">${leadName}</td>
        </tr>
        <tr>
          <td><strong>Email:</strong></td>
          <td><a href="mailto:${leadEmail}" style="color: #2563eb;">${leadEmail}</a></td>
        </tr>
        ${leadPhone ? `
        <tr>
          <td><strong>Teléfono:</strong></td>
          <td><a href="tel:${leadPhone}" style="color: #2563eb;">${leadPhone}</a></td>
        </tr>
        ` : ''}
        <tr>
          <td><strong>Fuente:</strong></td>
          <td><span class="badge badge-info">${leadSource}</span></td>
        </tr>
        <tr>
          <td><strong>Fecha:</strong></td>
          <td>${new Date().toLocaleString('es-ES', { dateStyle: 'full', timeStyle: 'short' })}</td>
        </tr>
      </table>
      ${leadMessage ? `
        <div class="divider"></div>
        <p class="text"><strong>Mensaje:</strong></p>
        <div style="background-color: #f9fafb; border-left: 4px solid #2563eb; padding: 16px; margin: 16px 0; border-radius: 4px;">
          <p style="margin: 0; color: #374151; white-space: pre-wrap;">${leadMessage}</p>
        </div>
      ` : ''}
      <div class="divider"></div>
      <p class="text">Accede al CRM para revisar y gestionar este nuevo contacto.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: `Nuevo lead: ${leadName} - ${leadSource}`,
      title: '🎯 Nuevo Lead Recibido',
      content,
      actionButton: {
        text: 'Ver Lead en CRM',
        url: leadUrl
      }
    });

    const text = `
Nuevo Lead Recibido - ${this.brandName}

Hola ${adminName},

¡Nuevo lead registrado!

Información del lead:
- Nombre: ${leadName}
- Email: ${leadEmail}
${leadPhone ? `- Teléfono: ${leadPhone}` : ''}
- Fuente: ${leadSource}
- Fecha: ${new Date().toLocaleString('es-ES')}

${leadMessage ? `Mensaje:\n${leadMessage}\n` : ''}

Accede al CRM para revisar este contacto.

Ver lead: ${leadUrl}

© ${new Date().getFullYear()} ${this.brandName}
    `.trim();

    return {
      subject: `🎯 Nuevo Lead: ${leadName} - ${leadSource}`,
      html,
      text
    };
  }

  projectAssigned(
    collaboratorName: string,
    projectName: string,
    clientName: string,
    viewUrl: string
  ): EmailTemplate {
    const content = `
      <p class="text">Hola <strong>${collaboratorName}</strong>,</p>
      <p class="text">Te acabamos de asignar a un nuevo proyecto en el CRM:</p>
      <div style="margin: 16px 0 8px; padding: 12px 14px; background-color: #f9fafb; border-left: 3px solid #2563eb; border-radius: 4px;">
        <p style="margin: 0 0 4px 0; font-size: 14px; font-weight: 700; color: #111827;">${projectName}</p>
        ${clientName ? `<p style="margin: 0; font-size: 13px; color: #6b7280;">Cliente: ${clientName}</p>` : ''}
      </div>
      <p class="text">Cuando entres al CRM verás el proyecto en tu panel con sus tareas, notas y documentos.</p>
    `;

    const html = this.getBaseTemplate({
      preheader: `Te han asignado al proyecto ${projectName}.`,
      title: 'Nueva asignación de proyecto',
      accent: 'primary',
      content,
      actionButton: { text: 'Ver mi panel', url: viewUrl },
      footer: 'Puedes gestionar tus preferencias de notificación desde Mi Perfil.',
    });

    const text = [
      `Hola ${collaboratorName},`,
      '',
      `Te acabamos de asignar al proyecto: ${projectName}${clientName ? ` (${clientName})` : ''}.`,
      '',
      `Entra al CRM para verlo: ${viewUrl}`,
      '',
      '— Cero Ideas',
    ].join('\n');

    return {
      subject: `Nueva asignación: ${projectName}`,
      html,
      text,
    };
  }

  taskStatusChanged(
    collaboratorName: string,
    taskTitle: string,
    projectName: string,
    prevStatus: string,
    newStatus: string,
    viewUrl: string
  ): EmailTemplate {
    const isDone = newStatus.toLowerCase().includes('hecho');
    const accent = isDone ? 'success' : 'primary';
    const content = `
      <p class="text">Hola <strong>${collaboratorName}</strong>,</p>
      <p class="text">Una de tus tareas ha cambiado de estado:</p>
      <div style="margin: 16px 0 8px; padding: 12px 14px; background-color: #f9fafb; border-left: 3px solid #2563eb; border-radius: 4px;">
        <p style="margin: 0 0 4px 0; font-size: 14px; font-weight: 700; color: #111827;">${taskTitle}</p>
        <p style="margin: 0; font-size: 13px; color: #6b7280;">Proyecto: ${projectName}</p>
      </div>
      <p class="text">
        <span class="badge badge-info">${prevStatus}</span>
        &rarr;
        <span class="badge ${isDone ? 'badge-success' : 'badge-info'}">${newStatus}</span>
      </p>
    `;
    const html = this.getBaseTemplate({
      preheader: `${taskTitle} → ${newStatus}`,
      title: `Tarea ${newStatus.toLowerCase()}`,
      accent,
      content,
      actionButton: { text: 'Ver la tarea', url: viewUrl },
      footer: 'Puedes desactivar estos avisos desde Mi Perfil > Notificaciones.',
    });
    const text = [
      `Hola ${collaboratorName},`,
      '',
      `Tu tarea "${taskTitle}" del proyecto ${projectName} cambió: ${prevStatus} → ${newStatus}.`,
      '',
      `Verla en el CRM: ${viewUrl}`,
    ].join('\n');
    return {
      subject: `${taskTitle} — ${newStatus}`,
      html,
      text,
    };
  }
}

export const emailTemplates = new EmailTemplateService();
